<template>
  <div id="app" class="app" :class="[className, getPlatformInfo]">
    <alert-sticky-bar v-if="layoutName && !['journey-layout', 'no-header-layout'].includes(layoutName)" />
    <transition name="layout">
      <component class="layout"
                 :is="layoutName"
                 @click.native="hideAllLayers"/>
    </transition>
    <dialogs-wrapper tag="div"></dialogs-wrapper>
    <dialogs-wrapper tag="div" name="error"></dialogs-wrapper>
    <transition name="error">
      <error-404 v-if="hasError === 404" />
      <error-500 v-else-if="hasError === 500" />
      <error-pluck-401 v-else-if="hasError === 'pluck401'" />
      <error-pluck-404 v-else-if="hasError === 'pluck404'" />
    </transition>
  </div>
</template>

<script>
// import '@/styles/app.scss'

import { SIGNIN_LINK } from '@/libs/constants'
import { mapState, mapMutations } from 'vuex'
import { STORAGE_KEY, storageManager } from '@/libs/storage-manager'
import {
  getAuthToken,
  removeAuthData
} from '@/libs/auth-helper'
import { clearAllApiCache } from '@/libs/api-request'
import { browserInfo } from '@/libs/utils'

import DefaultLayout from '@/layouts/DefaultLayout'
import JourneyLayout from '@/layouts/JourneyLayout'
import GroupLayout from '@/layouts/GroupLayout'
import UserInfoLayout from '@/layouts/UserInfoLayout'
import DashboardLayout from '@/layouts/DashboardLayout'
import NoHeaderLayout from '@/layouts/NoHeaderLayout'
import EmptyLayout from '@/layouts/EmptyLayout'

import AppDialog from '@/components/ui/AppDialog'
import AlertStickyBar from '@/components/header/AlertStickyBar'

import error404 from '@/components/error/404'
import error500 from '@/components/error/500'
import errorPluck401 from '@/components/error/pluck401'
import errorPluck404 from '@/components/error/pluck404'

export default {
  name: 'App',
  components: {
    AlertStickyBar,
    AppDialog,
    'default-layout': DefaultLayout,
    'journey-layout': JourneyLayout,
    'group-layout': GroupLayout,
    'userinfo-layout': UserInfoLayout,
    'dashboard-layout': DashboardLayout,
    'no-header-layout': NoHeaderLayout,
    'empty-layout': EmptyLayout,
    error500,
    error404,
    errorPluck401,
    errorPluck404
  },
  data () {
    return {
      hasError: false
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData',
      'errorData',
      'stickyDataId',
      'showStickyData',
      'myInfoTimestamp',
      'showNoPvDialog'
    ]),
    getPlatformInfo () {
      return 'platform__' + browserInfo.platform
    },
    layoutName () {
      if (!this.$route.matched.length) {
        return null
      }
      const route = Array.from(this.$route.matched).reverse()
        .find(item => item.meta.hasOwnProperty('layout'))

      const layout = route ? route.meta.layout : 'default'
      return `${layout}-layout`
    },
    className () {
      const items = []

      if (this.$route.meta.appClass) {
        items.push(this.$route.meta.appClass)
      }

      if (items.length) {
        return items.join(' ')
      }
      return null
    }
  },
  watch: {
    $route: {
      handler (current, prev) {
        if (current.name !== null) {
          // this.syncAuthTokenAndCookie()
          const guestOnly = current.meta.guestOnly
          const forAll = current.meta.forAll
          const authToken = getAuthToken()

          if (guestOnly) {
            if (authToken) {
              this.$router.replace({ name: 'dashboard' })
            }
          } else if (!forAll) {
            if (!authToken) {
              this.signOut()
              // window.location.href = SIGNIN_LINK
            }
          }
        }
      },
      immediate: true
    },
    errorData (value) {
      if (value.error && !this.hasError) {
        setTimeout(() => {
          this.hasError = value.code
        }, 500)
      } else {
        this.hasError = false
      }
    }
  },
  created () {
    // this.syncTimer = window.setInterval(() => {
    //   this.syncAuthTokenAndCookie()
    // }, 1000)

    window.addEventListener('beforeunload', event => {
      if (this.userData) {
        storageManager.setItem(STORAGE_KEY.showNoPvDialog, this.showNoPvDialog)
        storageManager.setItem(STORAGE_KEY.stickyDataId, this.stickyDataId)
        storageManager.setItem(STORAGE_KEY.showStickyData, this.showStickyData)
        storageManager.setItem(STORAGE_KEY.myInfoTimestamp,
          this.myInfoTimestamp)
      }
    })
  },
  destroyed () {
    window.clearInterval(this.syncTimer)
  },
  methods: {
    ...mapMutations([
      'setShowUserMenu',
      'setErrorData',
      'clearAllUserData'
    ]),
    hideAllLayers () {
      this.setShowUserMenu(false)
    },
    signOut () {
      if (this.userData) {
        storageManager.initUser(this.userData.userNum)
        storageManager.removeItem(STORAGE_KEY.showNoPvDialog)
        storageManager.removeItem(STORAGE_KEY.stickyDataId)
        storageManager.removeItem(STORAGE_KEY.showStickyData)
        storageManager.removeItem(STORAGE_KEY.myInfoTimestamp)
      }

      clearAllApiCache()
      this.clearAllUserData()
      removeAuthData()
    }
  }
}
</script>

<style lang="scss" scoped>
.layout {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  transition: opacity 0.3s ease;
  opacity: 1;

  & /deep/ .layout-transition-up {
    transition: margin-bottom 0.3s ease, transform 0.5s ease, opacity 0.5s ease;
  }

  & /deep/ .layout-transition-left {
    transition: transform 0.5s ease, opacity 0.5s ease;
  }

  & /deep/ .layout-transition-fade {
    transition: height 0.3s ease, opacity 0.5s ease;
  }

  &.layout-enter,
  &.layout-leave-to {
    & /deep/ .layout-transition-up {
      opacity: 0;
      transform: translateY(-200px);
    }

    & /deep/ .layout-transition-left {
      opacity: 0;
      transform: translateX(-200px);
    }

    & /deep/ .layout-transition-fade {
      opacity: 0;
    }
  }
}

.error-leave-active,
.error-enter-active {
  transition: transform 0.5s ease;
  transform: translateY(0);
  opacity: 1;
}

.error-enter,
.error-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
